import { Avatar, Chip, TableBody, TableContainer, TableHead, TableRow } from '@mui/material';
import { flatten, get, map, sum } from 'lodash-es';
import React from 'react';
import MeterLink from '~/components/MeterLink';
import { formatNumber } from '~/components/NumberText';
import { DAYS_PER_YEAR } from '~/constants/date';
import { formatUiDate } from '~/lib/date';
import { calculateMeterConsumption } from './meterOfferCalculationUtils';
import { EAReportGroupedMeter } from './reportTypes';
import {
  NoPageBreakBefore,
  NoPageBreakInside,
  PageBreakBefore,
  Section,
  SectionContainer,
  StyledTable,
  StyledTableCell,
  StyledTableRow,
  Title,
  TitleUnderline
} from './style';

const SitesSection: React.FC<{ groupedMeters: EAReportGroupedMeter[] }> = ({ groupedMeters }) => {
  return (
    <>
      <Section id="meters" sx={{ width: '100%', ...PageBreakBefore, ...NoPageBreakInside }}>
        <SectionContainer sx={{ textAlign: 'center' }}>
          <Title variant="h4">SITES</Title>
          <TitleUnderline />
        </SectionContainer>
      </Section>

      <Section sx={NoPageBreakBefore}>
        <SectionContainer>
          <TableContainer>
            <StyledTable>
              <TableHead>
                <TableRow>
                  <StyledTableCell>
                    State
                    <br />
                    /Group
                  </StyledTableCell>
                  <StyledTableCell>Trading Name</StyledTableCell>
                  <StyledTableCell>Legal Name</StyledTableCell>
                  <StyledTableCell>ABN/ACN</StyledTableCell>
                  <StyledTableCell>Site Address</StyledTableCell>
                  <StyledTableCell>Suburb</StyledTableCell>
                  <StyledTableCell>State</StyledTableCell>
                  <StyledTableCell>Postcode</StyledTableCell>
                  <StyledTableCell>NMI</StyledTableCell>
                  <StyledTableCell>Current Retailer</StyledTableCell>
                  <StyledTableCell>Contract End Date</StyledTableCell>
                  <StyledTableCell>Annual Usage</StyledTableCell>
                  <StyledTableCell>Total</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {groupedMeters.map(({ meters, name }, index) => (
                  <React.Fragment key={`site-${name}-${index}`}>
                    {map(meters, (meter, index) => (
                      <StyledTableRow key={meter.meterNumber}>
                        {index === 0 && (
                          <StyledTableCell rowSpan={meters.length}>{name || meter.state}</StyledTableCell>
                        )}
                        <StyledTableCell>{meter.company?.tradingName}</StyledTableCell>
                        <StyledTableCell>{meter.company?.legalName}</StyledTableCell>
                        <StyledTableCell sx={{ textWrap: 'nowrap' }}>
                          {[meter.company?.abn, meter.company?.acn].filter((v) => !!v)?.[0]}
                        </StyledTableCell>
                        <StyledTableCell>{meter.streetAddress}</StyledTableCell>
                        <StyledTableCell>{meter.suburb}</StyledTableCell>
                        <StyledTableCell>{meter.state}</StyledTableCell>
                        <StyledTableCell>{meter.postCode}</StyledTableCell>
                        <StyledTableCell>
                          <MeterLink
                            meterId={meter.id}
                            meterNumber={meter.meterNumber}
                            energyType={(meter as any).energyType}
                            variant="body2"
                          />
                        </StyledTableCell>
                        <StyledTableCell>{get(meter, 'contract.retailer.name')}</StyledTableCell>
                        <StyledTableCell>{formatUiDate(get(meter, 'contract.contractEndDate'))}</StyledTableCell>
                        <StyledTableCell>
                          <>
                            <Chip
                              sx={{ mb: 0.5 }}
                              size="small"
                              avatar={<Avatar>P</Avatar>}
                              label={formatNumber(meter.annualData.peakEnergy ?? 0, 0, '-')}
                            />
                            <br />
                            <Chip
                              sx={{ mb: 0.5 }}
                              size="small"
                              avatar={<Avatar>OP</Avatar>}
                              label={formatNumber(meter.annualData.offPeakEnergy ?? 0, 0, '-')}
                            />
                            <br />
                            <Chip
                              size="small"
                              avatar={<Avatar>S</Avatar>}
                              label={formatNumber(meter.annualData.shoulderEnergy ?? 0, 0, '-')}
                            />
                          </>
                        </StyledTableCell>
                        <StyledTableCell sx={{ fontWeight: 'bold', bgcolor: 'grey.A400' }}>
                          {formatNumber(
                            calculateMeterConsumption(meter, DAYS_PER_YEAR, { usePerMeterLossFactor: false }),
                            0,
                            '-'
                          )}
                        </StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </React.Fragment>
                ))}

                <TableRow sx={{ bgcolor: 'common.black' }}>
                  <StyledTableCell colSpan={10}></StyledTableCell>
                  <StyledTableCell sx={{ color: 'brand.main', fontWeight: 'bold' }}>Total</StyledTableCell>
                  <StyledTableCell>
                    {(() => {
                      const allMeters = flatten(map(groupedMeters, 'meters'));
                      const totalPeak = sum(allMeters.map((m) => m.annualData?.peakEnergy ?? 0));
                      const totalOffPeak = sum(allMeters.map((m) => m.annualData?.offPeakEnergy ?? 0));
                      const totalShoulder = sum(allMeters.map((m) => m.annualData?.shoulderEnergy ?? 0));
                      return (
                        <>
                          <Chip
                            sx={{ mb: 0.5, color: 'common.white', fontWeight: 'bold' }}
                            size="small"
                            avatar={<Avatar>P</Avatar>}
                            label={formatNumber(totalPeak, 0, '-')}
                          />
                          <Chip
                            sx={{ mb: 0.5, color: 'common.white', fontWeight: 'bold' }}
                            size="small"
                            avatar={<Avatar>OP</Avatar>}
                            label={formatNumber(totalOffPeak, 0, '-')}
                          />
                          <Chip
                            sx={{ mb: 0.5, color: 'common.white', fontWeight: 'bold' }}
                            size="small"
                            avatar={<Avatar>S</Avatar>}
                            label={formatNumber(totalShoulder, 0, '-')}
                          />
                        </>
                      );
                    })()}
                  </StyledTableCell>
                  <StyledTableCell sx={{ color: 'common.white', fontWeight: 'bold' }}>
                    {formatNumber(
                      sum(
                        flatten(map(groupedMeters, 'meters')).map((m) =>
                          calculateMeterConsumption(m, DAYS_PER_YEAR, { usePerMeterLossFactor: false })
                        )
                      ),
                      0,
                      '-'
                    )}
                  </StyledTableCell>
                </TableRow>
              </TableBody>
            </StyledTable>
          </TableContainer>
        </SectionContainer>
      </Section>
    </>
  );
};

export default SitesSection;
